import { useEffect, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { dateFnsLocale } from '@shared/i18n/lib/date-locale';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import {
  Button,
  CopyableHash,
  Input,
  Label,
  SecurityPageSkeleton,
  StatusBanner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@shared/ui';
import { swrKeys } from '@shared/api/swr/keys';
import { useApiServices } from '@shared/lib/use-api-services';
import { getApiErrorMessage } from '@shared/lib/get-api-error-message';
import { formatDateTime } from '@shared/lib/format-date-time';
import { useCopyToClipboard } from '@shared/lib/use-copy-to-clipboard';
import { validateUsdtAddress } from '@shared/lib/validate-usdt-address';
import { useAuth } from '@entities/auth';
import { useSecondFactorAction } from '@features/security';
import { describeWithdrawalAddressError, sanitizeWithdrawalAddressInput } from '@features/withdrawal';
import {
  IP_WHITELIST_COMMENT_MAX,
  WHITELIST_ADDRESS_LABEL_MAX,
  cidrValidationMessageKey,
  formatSessionLabel,
  isAlertLoginEvent,
  isSupportLoginActor,
  loginHistoryTitle,
  loginReasonKey,
  qrImageSrc,
  formatWithdrawalAddressHoldHours,
  sanitizeWhitelistTextField,
  userFacingAddressLabel,
  validateCidr,
  validateWhitelistTextField,
} from '@entities/security';
import { readApiError } from '@shared/api/errors/read-api-error';
import type {
  LoginHistoryItem,
  SecondFactor,
  SecondFactorMethod,
  SecuritySettings,
  TwoFaSetupDto,
  TwoFaSetupMethod,
} from '@shared/api/contracts/security';
import { ChangePasswordModal } from '@features/auth/change-password';
import { WithdrawalsLockBanner } from '@features/withdrawal';

// Бэк сохраняет этот комментарий по-русски при миграции общего списка; подменяем на перевод.
const LEGACY_MIGRATED_COMMENT = 'перенесено из общего списка';

const NETWORKS = ['TRC20', 'BEP20', 'ERC20', 'BTC', 'LTC'] as const;

const factorMethods = (method: string | null | undefined, isAdmin: boolean): SecondFactorMethod[] => {
  const primary: SecondFactorMethod = method?.toLowerCase() === 'email' ? 'email' : 'totp';
  const methods: SecondFactorMethod[] = primary === 'email' ? ['email', 'backup'] : ['totp', 'backup'];
  return isAdmin ? methods.filter((item) => item !== 'email') : methods;
};

export function SecuritySettings() {
  const { t, i18n } = useTranslation('cabinet');
  const { toast } = useToast();
  const { user } = useAuth();
  const services = useApiServices();
  const { run } = useSecondFactorAction();
  const { copy } = useCopyToClipboard();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [twoFaDraft, setTwoFaDraft] = useState<TwoFaSetupDto | null>(null);
  const [newFactorCode, setNewFactorCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [cidr, setCidr] = useState('');
  const [comment, setComment] = useState('');
  const [network, setNetwork] = useState<(typeof NETWORKS)[number]>('TRC20');
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  const [ipFormErrors, setIpFormErrors] = useState<{ cidr?: string; comment?: string }>({});
  const [addressFormErrors, setAddressFormErrors] = useState<{ address?: string; label?: string }>({});
  const [addingAddress, setAddingAddress] = useState(false);
  const [deletingAddressId, setDeletingAddressId] = useState<number | null>(null);
  const [olderHistory, setOlderHistory] = useState<LoginHistoryItem[]>([]);
  const [historyCursor, setHistoryCursor] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const locale = dateFnsLocale(i18n.language);

  const settings = useSWR(swrKeys.security.settings(), () => services.security.getSettings());
  const sessions = useSWR(swrKeys.security.sessions(), () => services.security.getSessions());
  const devices = useSWR(swrKeys.security.trustedDevices(), () => services.security.getTrustedDevices());
  const ipList = useSWR(swrKeys.security.ipWhitelist(), () => services.security.getIpWhitelist());
  const addresses = useSWR(swrKeys.security.withdrawalAddresses(), () => services.security.getWithdrawalAddresses());
  const history = useSWR(swrKeys.security.loginHistory(), () => services.security.getLoginHistory({ limit: 20 }));

  const firstHistoryId = history.data?.data[0]?.id ?? '';
  useEffect(() => {
    setOlderHistory([]);
    setHistoryCursor(history.data?.nextCursor ?? null);
  }, [firstHistoryId, history.data?.nextCursor]);

  const fail = (error: unknown) => {
    const parsed = readApiError(error);
    if (parsed?.code === 'ADDRESS_NOT_WHITELISTED' && parsed.addresses?.length) {
      toast(t('security.errors.autoAddresses', { addresses: parsed.addresses.join(', ') }), 'error');
      return;
    }
    toast(getApiErrorMessage(error, t, 'security.errors.generic'), 'error');
  };

  const withFactor = async <T,>(
    purpose: string,
    required: boolean,
    request: (factor?: SecondFactor) => Promise<T>,
  ) => {
    const result = await run({
      purpose,
      required,
      methods: factorMethods(settings.data?.twoFa.method, Boolean(user?.isAdmin)),
      request,
    });
    if (!result.ok) {
      if (!result.cancelled && result.error) fail(result.error);
      return null;
    }
    return result.data;
  };

  const requireFactor = (factor: SecondFactor | undefined): SecondFactor => {
    if (!factor) {
      throw new Error('missing factor');
    }
    return factor;
  };

  const mergeSettings = (patch: { sessionIpBinding?: 'OFF' | 'STRICT'; withdrawal2faRequired?: boolean }) => {
    void settings.mutate(
      (current: SecuritySettings | undefined) => (current ? { ...current, ...patch } : current),
      { revalidate: false },
    );
  };

  const relative = (value?: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return formatDistanceToNow(date, { addSuffix: true, locale });
  };

  const data = settings.data;

  if (settings.isLoading && !data) {
    return <SecurityPageSkeleton />;
  }

  const ipItems = ipList.data?.items ?? [];
  const addressItems = addresses.data?.items ?? [];
  const setupMethods: TwoFaSetupMethod[] = user?.isAdmin ? ['totp'] : ['totp', 'email'];
  const historyItems = [...(history.data?.data ?? []), ...olderHistory];

  const startMethodChange = (method: TwoFaSetupMethod) => {
    void services.security
      .setupTwoFa(method)
      .then((setup) => {
        setTwoFaDraft(setup);
        setNewFactorCode('');
        toast(t('security.twoFaChangeStarted'), 'success');
      })
      .catch(fail);
  };

  return (
    <div className="space-y-6">
      <WithdrawalsLockBanner lockedUntil={data?.withdrawalsLockedUntil} />

      <section className="space-y-4 rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold">{t('security.section')}</h2>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium">{t('security.password')}</p>
            <p className="text-xs text-muted-foreground">{t('security.passwordHint')}</p>
          </div>
          <Button type="button" variant="default" onClick={() => setPasswordOpen(true)}>
            {t('header.changePassword')}
          </Button>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium">{t('security.twoFaStatus', { method: data?.twoFa.method ?? '—' })}</p>
            <p className="text-xs text-muted-foreground">
              {t('security.backupLeft', { count: data?.twoFa.backupCodesLeft ?? 0 })}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {setupMethods.map((method) => (
              <Button key={method} type="button" variant="secondary" onClick={() => startMethodChange(method)}>
                {t(method === 'totp' ? 'security.methodTotp' : 'security.methodEmail')}
              </Button>
            ))}
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                void withFactor('backup_codes.regenerate', true, (factor) =>
                  services.security.regenerateBackupCodes(requireFactor(factor)),
                ).then((response) => {
                  if (response?.codes) setBackupCodes(response.codes);
                })
              }
            >
              {t('security.regenerateCodes')}
            </Button>
          </div>
        </div>
        {backupCodes ? (
          <pre className="whitespace-pre-wrap rounded-xl bg-muted p-3 text-sm">{backupCodes.join('\n')}</pre>
        ) : null}
        {twoFaDraft ? (
          <div className="space-y-2 rounded-xl border border-border p-3">
            {twoFaDraft.method === 'totp' && qrImageSrc(twoFaDraft.qr) ? (
              <img src={qrImageSrc(twoFaDraft.qr) ?? ''} alt="" className="h-40 w-40" />
            ) : null}
            {twoFaDraft.method === 'totp' ? (
              <p className="break-all text-sm text-muted-foreground">{twoFaDraft.code}</p>
            ) : (
              <p className="text-sm text-muted-foreground">{twoFaDraft.sentTo}</p>
            )}
            <Label htmlFor="security-new-factor-code">{t('security.newMethodCode')}</Label>
            <Input
              id="security-new-factor-code"
              value={newFactorCode}
              autoComplete="one-time-code"
              onChange={(event) => setNewFactorCode(event.target.value)}
            />
            <Button
              type="button"
              disabled={newFactorCode.trim().length < 6}
              onClick={() => {
                const method = twoFaDraft.method;
                void withFactor('2fa.change', true, (factor) =>
                  services.security.changeTwoFa({
                    method,
                    code: newFactorCode.trim(),
                    secondFactor: requireFactor(factor),
                  }),
                ).then((result) => {
                  if (!result) return;
                  setTwoFaDraft(null);
                  setNewFactorCode('');
                  void settings.mutate();
                  toast(t('security.twoFaChanged'), 'success');
                });
              }}
            >
              {t('security.factor.confirm')}
            </Button>
          </div>
        ) : null}
        <label className="flex items-center justify-between gap-3 text-sm">
          <span>
            {t('security.withdrawal2fa')}
            {!data?.addressWhitelistEnabled ? (
              <span className="mt-1 block text-xs text-muted-foreground">{t('security.withdrawal2faHint')}</span>
            ) : null}
          </span>
          <input
            type="checkbox"
            checked={Boolean(data?.withdrawal2faRequired)}
            disabled={!data?.addressWhitelistEnabled && Boolean(data?.withdrawal2faRequired)}
            onChange={(event) => {
              const next = event.target.checked;
              void withFactor('security_settings.change', !next, (factor) =>
                services.security.patchSettings({
                  withdrawal2faRequired: next,
                  ...(factor ? { secondFactor: factor } : {}),
                }),
              ).then((updated) => {
                if (updated) mergeSettings(updated);
              });
            }}
          />
        </label>
        <label className="flex items-center justify-between gap-3 text-sm">
          <span>
            {t('security.ipBinding')}
            <span className="mt-1 block text-xs text-muted-foreground">{t('security.ipBindingHint')}</span>
          </span>
          <input
            type="checkbox"
            checked={data?.sessionIpBinding === 'STRICT'}
            onChange={(event) => {
              const next = event.target.checked ? 'STRICT' : 'OFF';
              void withFactor('security_settings.change', next === 'OFF', (factor) =>
                services.security.patchSettings({
                  sessionIpBinding: next,
                  ...(factor ? { secondFactor: factor } : {}),
                }),
              ).then((updated) => {
                if (updated) mergeSettings(updated);
              });
            }}
          />
        </label>
      </section>

      <section className="space-y-3 rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">{t('security.ipTitle')}</h2>
          <input
            aria-label={t('security.ipEnabled')}
            type="checkbox"
            checked={Boolean(ipList.data?.enabled)}
            onChange={(event) => {
              if (event.target.checked && ipItems.length === 0) {
                toast(t('security.errors.ipEmpty'), 'error');
                return;
              }
              void withFactor('ip_whitelist.change', true, (factor) =>
                services.security.setIpWhitelistEnabled(event.target.checked, requireFactor(factor)),
              ).then((updated) => {
                if (updated) void ipList.mutate(updated, { revalidate: false });
                void settings.mutate();
              });
            }}
          />
        </div>
        <p className="text-xs text-muted-foreground">{t('security.ipHint')}</p>
        {user?.apiToken && !ipList.data?.enabled ? (
          <StatusBanner variant="pending">{t('security.ipRecommend')}</StatusBanner>
        ) : null}
        <ul className="space-y-2">
          {ipItems.map((item) => (
            <li key={item.id} className="flex items-center justify-between gap-3 text-sm">
              <span>
                {item.cidr}
                {item.comment ? (
                  <span className="text-muted-foreground"> · {item.comment === LEGACY_MIGRATED_COMMENT ? t('security.ipMigratedComment') : item.comment}</span>
                ) : null}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={Boolean(ipList.data?.enabled) && ipItems.length === 1}
                title={ipList.data?.enabled && ipItems.length === 1 ? t('security.ipLast') : undefined}
                onClick={() =>
                  void withFactor('ip_whitelist.change', true, (factor) =>
                    services.security.deleteIpWhitelist(item.id, requireFactor(factor)),
                  ).then((deleted) => {
                    if (deleted !== null) void ipList.mutate();
                  })
                }
              >
                {t('security.remove')}
              </Button>
            </li>
          ))}
        </ul>
        <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] sm:items-start">
          <div className="space-y-1">
            <Input
              value={cidr}
              placeholder={t('security.cidr')}
              invalid={Boolean(ipFormErrors.cidr)}
              onChange={(event) => {
                setCidr(event.target.value);
                setIpFormErrors((current) => ({ ...current, cidr: undefined }));
              }}
            />
            {ipFormErrors.cidr ? <p className="text-xs text-destructive">{ipFormErrors.cidr}</p> : null}
          </div>
          <div className="space-y-1">
            <Input
              value={comment}
              placeholder={t('security.comment')}
              maxLength={IP_WHITELIST_COMMENT_MAX}
              invalid={Boolean(ipFormErrors.comment)}
              onChange={(event) => {
                setComment(sanitizeWhitelistTextField(event.target.value, IP_WHITELIST_COMMENT_MAX));
                setIpFormErrors((current) => ({ ...current, comment: undefined }));
              }}
            />
            {ipFormErrors.comment ? <p className="text-xs text-destructive">{ipFormErrors.comment}</p> : null}
          </div>
          <Button
            type="button"
            className="sm:mt-0"
            onClick={() => {
              const trimmedCidr = cidr.trim();
              const trimmedComment = comment.trim();
              const nextErrors: { cidr?: string; comment?: string } = {};
              const cidrKey = cidrValidationMessageKey(validateCidr(trimmedCidr));
              if (cidrKey) {
                nextErrors.cidr = t(cidrKey);
              }
              if (validateWhitelistTextField(trimmedComment, IP_WHITELIST_COMMENT_MAX) !== 'ok') {
                nextErrors.comment = t('security.validation.commentInvalid');
              }
              if (Object.keys(nextErrors).length > 0) {
                setIpFormErrors(nextErrors);
                return;
              }
              setIpFormErrors({});
              void withFactor('ip_whitelist.change', true, (factor) =>
                services.security.addIpWhitelist({
                  cidr: trimmedCidr,
                  comment: trimmedComment || undefined,
                  secondFactor: requireFactor(factor),
                }),
              ).then((created) => {
                if (!created) return;
                setCidr('');
                setComment('');
                void ipList.mutate();
              });
            }}
          >
            {t('security.addIp')}
          </Button>
        </div>
      </section>

      <section className="space-y-3 rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">{t('security.addressTitle')}</h2>
        </div>
        {addresses.data?.enabled === false ? (
          <StatusBanner variant="pending">{t('security.addressDisabledLock')}</StatusBanner>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">{t('security.addressRequired')}</p>
            <p className="text-xs text-muted-foreground">
              {t('security.addressHold', {
                hours: formatWithdrawalAddressHoldHours(addresses.data?.holdHours),
              })}
            </p>
          </>
        )}
        <ul className="space-y-2">
          {addressItems.map((item) => {
            const label = userFacingAddressLabel(item.label);
            return (
            <li key={item.id} className="flex items-center justify-between gap-3 text-sm">
              <span className="min-w-0 flex-1 space-y-1">
                <p className="font-medium">
                  {item.network}
                  {label ? ` · ${label}` : ''}
                </p>
                <CopyableHash
                  value={item.address}
                  onCopy={() => copy(item.address, undefined, `security-address-${item.id}`)}
                />
                <span className="mt-1 flex flex-wrap items-center gap-2">
                  {item.amlBlocked ? (
                    <span className="rounded-full bg-fail px-2 py-0.5 text-xs text-fail-foreground">
                      {t('security.addressAmlBlocked')}
                    </span>
                  ) : item.status === 'pending' ? (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      <AddressCountdown
                        activeFrom={item.activeFrom}
                        language={i18n.language}
                        onElapsed={() => void addresses.mutate()}
                      />
                    </span>
                  ) : (
                    <span className="rounded-full bg-success px-2 py-0.5 text-xs text-success-foreground">
                      {t('security.addressActive')}
                    </span>
                  )}
                </span>
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                loading={deletingAddressId === item.id}
                disabled={item.amlBlocked === true || deletingAddressId !== null || addingAddress}
                onClick={() => {
                  setDeletingAddressId(item.id);
                  void withFactor('address_whitelist.change', true, (factor) =>
                    services.security.deleteWithdrawalAddress(item.id, requireFactor(factor)),
                  )
                    .then((deleted) => {
                      if (deleted !== null) void addresses.mutate();
                    })
                    .finally(() => {
                      setDeletingAddressId(null);
                    });
                }}
              >
                {t('security.remove')}
              </Button>
            </li>
            );
          })}
        </ul>
        <div className="grid gap-2 sm:grid-cols-[8rem_1fr_1fr_auto] sm:items-start">
          <Select
            value={network}
            onValueChange={(value) => {
              setNetwork(value as (typeof NETWORKS)[number]);
              setAddressFormErrors((current) => ({ ...current, address: undefined }));
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {NETWORKS.map((item) => (
                <SelectItem key={item} value={item}>
                  {item}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="space-y-1">
            <Input
              value={address}
              placeholder={t('security.address')}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              className="font-mono text-[13px]"
              invalid={Boolean(addressFormErrors.address)}
              onChange={(event) => {
                setAddress(sanitizeWithdrawalAddressInput(event.target.value));
                setAddressFormErrors((current) => ({ ...current, address: undefined }));
              }}
            />
            {addressFormErrors.address ? (
              <p className="text-xs text-destructive">{addressFormErrors.address}</p>
            ) : null}
          </div>
          <div className="space-y-1">
            <Input
              value={label}
              placeholder={t('security.label')}
              maxLength={WHITELIST_ADDRESS_LABEL_MAX}
              invalid={Boolean(addressFormErrors.label)}
              onChange={(event) => {
                setLabel(sanitizeWhitelistTextField(event.target.value, WHITELIST_ADDRESS_LABEL_MAX));
                setAddressFormErrors((current) => ({ ...current, label: undefined }));
              }}
            />
            {addressFormErrors.label ? <p className="text-xs text-destructive">{addressFormErrors.label}</p> : null}
          </div>
          <Button
            type="button"
            className="sm:mt-0"
            loading={addingAddress}
            disabled={addingAddress || deletingAddressId !== null}
            onClick={() => {
              void (async () => {
                const trimmedAddress = sanitizeWithdrawalAddressInput(address).trim();
                const trimmedLabel = label.trim();
                const nextErrors: { address?: string; label?: string } = {};

                if (!trimmedAddress) {
                  nextErrors.address = t('withdrawal.validation.required');
                } else {
                  const result = await validateUsdtAddress(trimmedAddress, network);
                  if (!result.isValid) {
                    nextErrors.address = describeWithdrawalAddressError(
                      trimmedAddress,
                      network,
                      result.errorCode,
                      t,
                    ).message;
                  }
                }

                if (validateWhitelistTextField(trimmedLabel, WHITELIST_ADDRESS_LABEL_MAX) !== 'ok') {
                  nextErrors.label = t('security.validation.labelInvalid');
                }

                if (Object.keys(nextErrors).length > 0) {
                  setAddressFormErrors(nextErrors);
                  return;
                }
                setAddressFormErrors({});

                setAddingAddress(true);
                try {
                  const created = await withFactor('address_whitelist.change', true, (factor) =>
                    services.security.addWithdrawalAddress({
                      network,
                      address: trimmedAddress,
                      label: trimmedLabel || undefined,
                      secondFactor: requireFactor(factor),
                    }),
                  );
                  if (!created) return;
                  setAddress('');
                  setLabel('');
                  void addresses.mutate();
                  toast(
                    t('security.addressAdded', { datetime: formatDateTime(created.activeFrom ?? '', i18n.language) }),
                    'success',
                  );
                } finally {
                  setAddingAddress(false);
                }
              })();
            }}
          >
            {t('security.addAddress')}
          </Button>
        </div>
      </section>

      <section className="space-y-3 rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">{t('security.sessionsTitle')}</h2>
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              void withFactor('sessions.terminate_others', true, (factor) =>
                services.security.terminateOtherSessions(requireFactor(factor)),
              ).then((response) => {
                if (response) void sessions.mutate();
              })
            }
          >
            {t('security.terminateOthers')}
          </Button>
        </div>
        <ul className="space-y-2">
          {(sessions.data ?? []).map((item) => (
            <li key={item.id} className="flex items-center justify-between gap-3 text-sm">
              <span>
                {formatSessionLabel(item.browser, item.os) || t('security.unknownDevice')}
                {item.current ? <span className="ml-2 text-xs font-medium text-green-700 dark:text-green-400">{t('security.thisDevice')}</span> : null}
                <span className="mt-1 block text-xs text-muted-foreground">
                  {item.ip ?? '—'} · {relative(item.lastSeenAt)}
                </span>
              </span>
              {item.current ? null : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => void services.security.revokeSession(item.id).then(() => sessions.mutate()).catch(fail)}
                >
                  {t('security.revoke')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className="space-y-3 rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold">{t('security.devicesTitle')}</h2>
        <ul className="space-y-2">
          {(devices.data ?? []).map((item) => (
            <li key={item.id} className="flex items-center justify-between gap-3 text-sm">
              <span>
                {formatSessionLabel(item.browser, item.os) || t('security.unknownDevice')}
                {item.current ? <span className="ml-2 text-xs font-medium text-green-700 dark:text-green-400">{t('security.thisDevice')}</span> : null}
                <span className="mt-1 block text-xs text-muted-foreground">{item.lastIp ?? '—'}</span>
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => void services.security.revokeTrustedDevice(item.id).then(() => devices.mutate()).catch(fail)}
              >
                {t('security.revokeDevice')}
              </Button>
            </li>
          ))}
        </ul>
      </section>

      <section className="space-y-3 rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold">{t('security.historyTitle')}</h2>
        <ul className="space-y-2">
          {historyItems.map((item) => (
            <li
              key={item.id}
              className={`rounded-lg border border-border p-3 text-sm ${isAlertLoginEvent(item.eventType) ? 'border-destructive/40 bg-destructive/5' : ''}`}
            >
              <p className="font-medium">
                {formatDateTime(item.occurredAt, i18n.language)} · {loginHistoryTitle(item, t)}
              </p>
              <p className="text-xs text-muted-foreground">
                {isSupportLoginActor(item.actor) ? t('security.administrator') : item.ip ?? '—'}
                {item.reason ? ` · ${t(loginReasonKey(item.reason), { defaultValue: item.reason })}` : ''}
                {formatSessionLabel(item.browser, item.os) ? ` · ${formatSessionLabel(item.browser, item.os)}` : ''}
              </p>
            </li>
          ))}
        </ul>
        {historyCursor ? (
          <Button
            type="button"
            variant="secondary"
            disabled={historyLoading}
            onClick={() => {
              setHistoryLoading(true);
              void services.security
                .getLoginHistory({ before: historyCursor, limit: 20 })
                .then((page) => {
                  setOlderHistory((current) => [...current, ...page.data]);
                  setHistoryCursor(page.nextCursor ?? null);
                })
                .catch(fail)
                .finally(() => setHistoryLoading(false));
            }}
          >
            {t('security.showMore')}
          </Button>
        ) : null}
      </section>

      <ChangePasswordModal open={passwordOpen} onOpenChange={setPasswordOpen} />
    </div>
  );
}

function AddressCountdown({
  activeFrom,
  language,
  onElapsed,
}: {
  activeFrom?: string | null;
  language: string;
  onElapsed: () => void;
}) {
  const { t } = useTranslation('cabinet');
  const [now, setNow] = useState(() => Date.now());
  const elapsed = useRef(false);
  const onElapsedRef = useRef(onElapsed);
  onElapsedRef.current = onElapsed;

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!activeFrom || elapsed.current) return;
    if (new Date(activeFrom).getTime() > now) return;
    elapsed.current = true;
    onElapsedRef.current();
  }, [activeFrom, now]);

  return (
    <>
      {t('security.addressPending', { datetime: formatDateTime(activeFrom ?? '', language) })}
      {activeFrom ? ` · ${formatDistanceToNow(new Date(activeFrom), { locale: dateFnsLocale(language), addSuffix: true })}` : ''}
    </>
  );
}
